import { Button, Container, Label, NumericInput } from '@playcanvas/pcui';
import { Entity, Ray, TranslateGizmo, Vec3 } from 'playcanvas';

import { Events } from '../events';
import { Scene } from '../scene';
import { ToolOverlay, OverlayWriter } from '../tool-overlay';
import { i18n } from '../ui/localization';

// pointer movement below this many pixels still counts as a click
const CLICK_TOLERANCE = 4;

// screen-space radius for picking an existing point marker
const MARKER_RADIUS = 8;

const p = new Vec3();
const ray = new Ray();

class PointPlaceTransformHandler {
    activate() {}
    deactivate() {}
}

// place free points in space (on splats, or on the ground plane as
// fallback), then span a point-defined shape between them via the shape
// dialog ("fill"). points live in world space and are owned by the tool.
class PointPlaceTool {
    activate: () => void;
    deactivate: () => void;
    getFocus: () => { position: Vec3, radius: number } | null;

    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        const points: Vec3[] = [];
        let selection = -1;
        let active = false;
        let suppressUI = 0;

        const flatPoints = () => {
            const flat: number[] = [];
            for (const point of points) {
                flat.push(point.x, point.y, point.z);
            }
            return flat;
        };

        // ui toolbar

        const hintLabel = new Label({ class: 'select-toolbar-label' });
        i18n.bindText(hintLabel, 'pointPlace.hint');

        const coordInputs = ['x', 'y', 'z'].map(() => new NumericInput({
            width: 80,
            precision: 3,
            enabled: false,
            value: 0
        }));

        const deleteButton = new Button({ class: 'select-toolbar-button', enabled: false });
        i18n.bindText(deleteButton, 'pointPlace.delete');

        const clearButton = new Button({ class: 'select-toolbar-button', enabled: false });
        i18n.bindText(clearButton, 'measure.clear');

        const selectToolbar = new Container({
            class: ['select-toolbar', 'select-toolbar-tool'],
            hidden: true
        });
        selectToolbar.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
        });
        selectToolbar.append(hintLabel);
        coordInputs.forEach(input => selectToolbar.append(input));
        selectToolbar.append(deleteButton);
        selectToolbar.append(clearButton);
        canvasContainer.append(selectToolbar);

        // point markers and connecting outline render via the shared tool overlay

        const overlay = new ToolOverlay();
        overlay.provider = (writer: OverlayWriter) => {
            if (!active) {
                return;
            }
            for (const point of points) {
                writer.dot(point);
            }
            for (let i = 0; i < points.length - 1; i++) {
                writer.segment(points[i], points[i + 1]);
            }
            if (points.length >= 3) {
                writer.segment(points[points.length - 1], points[0]);
            }
        };

        // gizmo for dragging the selected point

        const gizmo = new TranslateGizmo(scene.camera.camera, scene.gizmoLayer);
        const entity = new Entity('pointPlaceGizmo');
        const transformHandler = new PointPlaceTransformHandler();

        gizmo.on('render:update', () => {
            scene.forceRender = true;
        });

        const updateGizmoSize = () => {
            const { camera, canvas } = scene;
            if (camera.ortho) {
                gizmo.size = 1125 / canvas.clientHeight;
            } else {
                gizmo.size = 1200 / Math.max(canvas.clientWidth, canvas.clientHeight);
            }
        };
        updateGizmoSize();
        events.on('camera.resize', updateGizmoSize);
        events.on('camera.ortho', updateGizmoSize);

        // sync the toolbar and notify listeners, without touching the gizmo
        // (safe to call during a drag)
        const syncUI = (updateInputs = true) => {
            if (active && selection >= 0 && selection < points.length) {
                if (updateInputs) {
                    suppressUI++;
                    coordInputs[0].value = points[selection].x;
                    coordInputs[1].value = points[selection].y;
                    coordInputs[2].value = points[selection].z;
                    suppressUI--;
                }
                coordInputs.forEach((input) => {
                    input.enabled = true;
                });
            } else {
                coordInputs.forEach((input) => {
                    input.enabled = false;
                });
            }

            deleteButton.enabled = selection >= 0;
            clearButton.enabled = points.length > 0;

            events.fire('pointPlace.changed', flatPoints());
            scene.forceRender = true;
        };

        // full state sync including the gizmo attachment (not drag-safe)
        const changed = (updateInputs = true) => {
            gizmo.detach();
            if (active && selection >= 0 && selection < points.length) {
                entity.setLocalPosition(points[selection]);
                gizmo.attach(entity);
            }
            syncUI(updateInputs);
        };

        gizmo.on('transform:move', () => {
            if (selection >= 0 && selection < points.length) {
                points[selection].copy(entity.getLocalPosition());
                // only sync the ui during the drag: re-attaching the gizmo
                // here would abort the drag after a single move event
                syncUI(true);
            }
        });

        gizmo.on('transform:end', () => {
            changed(true);
        });

        coordInputs.forEach((input, axis) => {
            input.on('change', (value: number) => {
                if (!suppressUI && selection >= 0 && selection < points.length) {
                    const point = points[selection];
                    point[axis === 0 ? 'x' : axis === 1 ? 'y' : 'z'] = value;
                    changed(false);
                }
            });
        });

        const deleteSelected = () => {
            if (selection >= 0 && selection < points.length) {
                points.splice(selection, 1);
                selection = Math.min(selection, points.length - 1);
                changed();
            }
        };

        deleteButton.on('click', deleteSelected);

        events.on('select.delete', () => {
            if (active) {
                deleteSelected();
            }
        });

        clearButton.on('click', () => {
            points.length = 0;
            selection = -1;
            changed();
        });

        // current points for the shape dialog (which drives this tool when a
        // point-defined shape kind is selected)
        events.function('pointPlace.points', () => flatPoints());

        // fired by the shape handler once a point shape was inserted: the
        // points have served their purpose
        events.on('pointPlace.clear', () => {
            points.length = 0;
            selection = -1;
            changed();
        });

        // pointer handling: click selects a nearby marker or places a new point

        const isPrimary = (e: PointerEvent) => {
            return e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary;
        };

        let clicked = false;
        let clickX = 0;
        let clickY = 0;

        const pointerdown = (e: PointerEvent) => {
            if (!clicked && isPrimary(e)) {
                clicked = true;
                clickX = e.offsetX;
                clickY = e.offsetY;
            }
        };

        const pointermove = (e: PointerEvent) => {
            if (clicked && Math.hypot(e.offsetX - clickX, e.offsetY - clickY) > CLICK_TOLERANCE) {
                clicked = false;
            }
        };

        const pointerup = async (e: PointerEvent) => {
            if (!clicked || !isPrimary(e)) {
                return;
            }
            clicked = false;

            // existing marker under the cursor: select it
            const cameraPos = scene.camera.mainCamera.getPosition();
            const cameraFwd = scene.camera.mainCamera.forward;
            for (let i = 0; i < points.length; i++) {
                p.sub2(points[i], cameraPos);
                if (p.dot(cameraFwd) <= 0) {
                    continue;  // behind the camera
                }
                scene.camera.worldToScreen(points[i], p);
                p.x *= canvasContainer.dom.clientWidth;
                p.y *= canvasContainer.dom.clientHeight;
                if (Math.abs(p.x - clickX) < MARKER_RADIUS && Math.abs(p.y - clickY) < MARKER_RADIUS) {
                    selection = i;
                    changed();
                    return;
                }
            }

            // place a new point: on the splat surface, or on the ground plane
            const result = await scene.camera.intersect(
                clickX / canvasContainer.dom.clientWidth,
                clickY / canvasContainer.dom.clientHeight
            );

            let position: Vec3 | null = null;
            if (result) {
                position = result.position.clone();
            } else {
                scene.camera.getRay(clickX, clickY, ray);
                if (Math.abs(ray.direction.y) > 1e-6) {
                    const t = -ray.origin.y / ray.direction.y;
                    if (t > 0) {
                        position = new Vec3(
                            ray.origin.x + ray.direction.x * t,
                            0,
                            ray.origin.z + ray.direction.z * t
                        );
                    }
                }
            }

            if (position) {
                points.push(position);
                selection = points.length - 1;
                changed();
            }

            e.preventDefault();
            e.stopPropagation();
        };

        // frame the placed points ('f' shortcut)
        this.getFocus = () => {
            if (points.length === 0) {
                return null;
            }
            const position = new Vec3();
            for (const point of points) {
                position.add(point);
            }
            position.mulScalar(1 / points.length);

            let radius = 0.5;
            for (const point of points) {
                radius = Math.max(radius, point.distance(position));
            }
            return { position, radius: radius * 1.5 };
        };

        this.activate = () => {
            active = true;
            canvasContainer.dom.addEventListener('pointerdown', pointerdown);
            canvasContainer.dom.addEventListener('pointermove', pointermove);
            canvasContainer.dom.addEventListener('pointerup', pointerup, true);
            selectToolbar.hidden = false;
            events.fire('transformHandler.push', transformHandler);
            scene.add(overlay);
            changed();
        };

        this.deactivate = () => {
            active = false;
            gizmo.detach();
            scene.remove(overlay);
            canvasContainer.dom.removeEventListener('pointerdown', pointerdown);
            canvasContainer.dom.removeEventListener('pointermove', pointermove);
            canvasContainer.dom.removeEventListener('pointerup', pointerup, true);
            selectToolbar.hidden = true;
            events.fire('transformHandler.pop');
            scene.forceRender = true;
        };
    }
}

export { PointPlaceTool };
